import React, { useState, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { EnhancedTemplate, WorksheetMapping } from '../../types/templateTypes';
import PaymentTypeMapper from './PaymentTypeMapper';
import ColumnMapper from './ColumnMapper';
import BusinessRulesEditor from './BusinessRulesEditor';
import TemplateTester from './TemplateTester';
import WorksheetMapper from './WorksheetMapper';
import { extractHeadersSafely } from '../../utils/worksheetValidation';
import {
  Upload,
  FileSpreadsheet,
  Info,
  CheckCircle,
  AlertCircle,
  Settings,
  TestTube,
  Save,
  ChevronRight,
  AlertTriangle,
  Layers
} from 'lucide-react';

interface TemplateBuilderProps {
  existingTemplate?: EnhancedTemplate;
  onSave: (template: EnhancedTemplate) => Promise<void>;
}

export const TemplateBuilder: React.FC<TemplateBuilderProps> = ({
  existingTemplate,
  onSave
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [sampleFile, setSampleFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<any[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [detectedSheets, setDetectedSheets] = useState<Array<{ name: string; rowCount: number; headers: string[] }>>([]);
  const [worksheetSuggestions, setWorksheetSuggestions] = useState<WorksheetMapping | undefined>(undefined);
  const [fileParseError, setFileParseError] = useState<string | null>(null);
  const [template, setTemplate] = useState<EnhancedTemplate>(
    existingTemplate || {
      id: '',
      name: '',
      aggregator: '',
      fileType: 'xlsx',
      parserConfig: {
        headerRow: 0,
        sheetName: undefined,
        delimiter: undefined
      },
      columnMappings: {},
      paymentTypeConfig: {
        columnName: '',
        mappingStrategy: 'exact',
        customMappings: {}
      },
      runHandling: {
        strategy: 'sequential',
        syntheticRunCount: undefined,
        runIdentifier: undefined
      },
      validation: {
        requiredColumns: [],
        dataQualityChecks: []
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      version: 1,
      status: 'draft'
    }
  );

  // Parse uploaded file
  const handleFileUpload = useCallback(async (file: File) => {
    // Clear previous errors
    setFileParseError(null);

    try {
      const reader = new FileReader();

      reader.onload = (e) => {
        const data = e.target?.result;
        if (!data) return;

        if (file.name.endsWith('.csv')) {
          // Parse CSV using Papa Parse (handles RFC 4180 quoted commas)
          const text = new TextDecoder().decode(data as ArrayBuffer);

          const parsed = Papa.parse<any>(text, {
            header: true,
            skipEmptyLines: true,
            transformHeader: (header: string) => header.trim(),
            transform: (value: string) => value.trim()
          });

          const headers = parsed.meta.fields || [];
          const rows = parsed.data;

          setColumns(headers);
          setParsedData(rows.slice(0, 100)); // Preview first 100 rows
          setTemplate(prev => ({
            ...prev,
            fileType: 'csv',
            parserConfig: { ...prev.parserConfig, delimiter: ',' }
          }));
        } else {
          // Parse Excel
          const workbook = XLSX.read(data, { type: 'array' });
          const isMultiSheet = workbook.SheetNames.length > 1;

          // Detect all sheets for multi-sheet files
          if (isMultiSheet) {
            const sheets = workbook.SheetNames.map(name => {
              const ws = workbook.Sheets[name];

              // Use validated header extraction (NEVER silently filter)
              const { headers, hasIssues, issues } = extractHeadersSafely(
                ws,
                name,
                template.parserConfig.headerRow
              );

              // Log validation issues for debugging
              if (hasIssues) {
                console.warn(`Worksheet "${name}" validation issues:`, issues);
              }

              // Calculate row count from worksheet range
              const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
              const rowCount = Math.max(0, range.e.r - template.parserConfig.headerRow);

              return { name, rowCount, headers };
            });

            setDetectedSheets(sheets);

            // Auto-suggest worksheet mappings based on sheet names
            const suggestions: WorksheetMapping = {};
            const upfrontPatterns = [/upfront/i, /agreement.*holder/i, /initial.*commission/i];
            const trailPatterns = [/trail/i, /recurring/i, /ongoing/i];
            const nilPatterns = [/nil/i, /zero/i];
            const summaryPatterns = [/summary/i, /totals/i, /overview/i];

            workbook.SheetNames.forEach(name => {
              if (upfrontPatterns.some(p => p.test(name)) && !suggestions.upfront) {
                suggestions.upfront = name;
              } else if (trailPatterns.some(p => p.test(name)) && !suggestions.trail) {
                suggestions.trail = name;
              } else if (nilPatterns.some(p => p.test(name)) && !suggestions.nil) {
                suggestions.nil = name;
              } else if (summaryPatterns.some(p => p.test(name)) && !suggestions.summary) {
                suggestions.summary = name;
              }
            });

            setWorksheetSuggestions(suggestions);

            // For preview, use first sheet with validated extraction
            const firstSheet = workbook.SheetNames[0];
            const firstWorksheet = workbook.Sheets[firstSheet];

            // Extract headers with validation
            const { headers: firstHeaders, hasIssues: previewHasIssues, issues: previewIssues } = extractHeadersSafely(
              firstWorksheet,
              firstSheet,
              template.parserConfig.headerRow
            );

            if (previewHasIssues) {
              console.warn(`Preview sheet "${firstSheet}" validation issues:`, previewIssues);
            }

            // Extract data rows using validated headers
            const firstJsonData = XLSX.utils.sheet_to_json(firstWorksheet, { header: 1 });
            const firstRows = firstJsonData.slice(template.parserConfig.headerRow + 1)
              .map((row: any) => {
                const rowArray = row as any[];
                return firstHeaders.reduce((obj, header, index) => {
                  obj[header] = rowArray[index] !== undefined ? String(rowArray[index]).trim() : '';
                  return obj;
                }, {} as any);
              });

            setColumns(firstHeaders);
            setParsedData(firstRows.slice(0, 100));
            setTemplate(prev => ({
              ...prev,
              fileType: 'xlsx',
              isMultiSheet: true,
              worksheetMapping: suggestions,
              parserConfig: { ...prev.parserConfig, sheetName: undefined }
            }));
          } else {
            // Single sheet Excel with validated header extraction
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];

            // Extract headers with validation (NEVER silently filter)
            const { headers, hasIssues: singleSheetHasIssues, issues: singleSheetIssues } = extractHeadersSafely(
              worksheet,
              sheetName,
              template.parserConfig.headerRow
            );

            if (singleSheetHasIssues) {
              console.warn(`Single sheet "${sheetName}" validation issues:`, singleSheetIssues);
            }

            // Extract data rows using validated headers
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            const rows = jsonData.slice(template.parserConfig.headerRow + 1)
              .map((row: any) => {
                const rowArray = row as any[];
                return headers.reduce((obj, header, index) => {
                  obj[header] = rowArray[index] !== undefined ? String(rowArray[index]).trim() : '';
                  return obj;
                }, {} as any);
              });

            setColumns(headers);
            setParsedData(rows.slice(0, 100));
            setDetectedSheets([]);
            setWorksheetSuggestions(undefined);
            setTemplate(prev => ({
              ...prev,
              fileType: 'xlsx',
              isMultiSheet: false,
              worksheetMapping: undefined,
              parserConfig: { ...prev.parserConfig, sheetName }
            }));
          }
        }

        setSampleFile(file);
        console.log('File parsed successfully');
      };

      if (file.name.endsWith('.csv')) {
        reader.readAsArrayBuffer(file);
      } else {
        reader.readAsArrayBuffer(file);
      }
    } catch (error) {
      console.error('Error parsing file:', error);
      setFileParseError(
        error instanceof Error
          ? error.message
          : 'Failed to parse file. Please check the file format and try again.'
      );
    }

    return false; // Prevent default upload
  }, [template.parserConfig.headerRow]);

  // Extract unique payment types from data
  const uniquePaymentTypes = useMemo(() => {
    if (!template.paymentTypeConfig.columnName || !parsedData.length) return [];

    const types = new Set<string>();
    parsedData.forEach(row => {
      const value = row[template.paymentTypeConfig.columnName];
      if (value) types.add(value);
    });

    return Array.from(types).sort();
  }, [parsedData, template.paymentTypeConfig.columnName]);

  // Validate payment type values look like actual payment types
  const paymentTypeWarning = useMemo(() => {
    if (!uniquePaymentTypes.length) return null;

    const loanTypeKeywords = ['mortgage', 'commercial', 'residential', 'refinance', 'purchase', 'investment'];
    const paymentTypeKeywords = ['trail', 'upfront', 'commission', 'clawback', 'deduction', 'adjustment'];

    const firstValue = uniquePaymentTypes[0].toLowerCase();
    const looksLikeLoanType = loanTypeKeywords.some(keyword => firstValue.includes(keyword));
    const looksLikePaymentType = paymentTypeKeywords.some(keyword => firstValue.includes(keyword));

    if (looksLikeLoanType && !looksLikePaymentType) {
      return {
        type: 'error',
        value: uniquePaymentTypes[0],
        message: 'This looks like a loan type, not a payment type. Payment types usually contain terms like "trail", "upfront", or "commission".'
      };
    }

    return null;
  }, [uniquePaymentTypes]);

  // Save template
  const handleSave = async () => {
    try {
      const finalTemplate: EnhancedTemplate = {
        ...template,
        id: template.id || `template_${Date.now()}`,
        updatedAt: new Date().toISOString(),
        status: 'active'
      };

      await onSave(finalTemplate);
      console.log('Template saved successfully');
    } catch (error) {
      console.error('Error saving template:', error);
    }
  };

  // Dynamically build steps based on file type
  const steps = useMemo(() => {
    const baseSteps = [
      {
        title: 'Upload Sample',
        icon: <Upload className="w-5 h-5" />,
        description: 'Upload a sample file to analyze structure'
      },
      {
        title: 'Template Info',
        icon: <Info className="w-5 h-5" />,
        description: 'Configure basic template settings'
      }
    ];

    // Add worksheet mapping step for multi-sheet files
    if (template.isMultiSheet && detectedSheets.length > 1) {
      baseSteps.push({
        title: 'Configure Worksheets',
        icon: <Layers className="w-5 h-5" />,
        description: 'Map worksheets to commission types'
      });
    }

    // Add remaining steps
    baseSteps.push(
      {
        title: 'Map Columns',
        icon: <FileSpreadsheet className="w-5 h-5" />,
        description: 'Map file columns to database fields'
      },
      {
        title: 'Payment Types',
        icon: <AlertCircle className="w-5 h-5" />,
        description: 'Configure payment type mappings'
      },
      {
        title: 'Business Rules',
        icon: <Settings className="w-5 h-5" />,
        description: 'Set validation and processing rules'
      },
      {
        title: 'Test & Save',
        icon: <TestTube className="w-5 h-5" />,
        description: 'Test configuration and save template'
      }
    );

    return baseSteps;
  }, [template.isMultiSheet, detectedSheets.length]);

  const StepIndicator = () => (
    <div className="flex items-center justify-between mb-8">
      {steps.map((step, index) => (
        <div key={index} className="flex items-center flex-1">
          <div className="flex items-center">
            <div
              className={`
                flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all
                ${index === currentStep
                  ? 'bg-blue-500 border-blue-500 text-white'
                  : index < currentStep
                  ? 'bg-green-500 border-green-500 text-white'
                  : 'bg-transparent border-slate-600 text-slate-400'
                }
              `}
            >
              {index < currentStep ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                step.icon
              )}
            </div>
            <div className="ml-3">
              <div className={`text-sm font-medium ${
                index === currentStep ? 'text-white' : 'text-slate-400'
              }`}>
                {step.title}
              </div>
              <div className="text-xs text-slate-500 hidden lg:block">
                {step.description}
              </div>
            </div>
          </div>
          {index < steps.length - 1 && (
            <ChevronRight className="w-5 h-5 text-slate-600 mx-4 flex-shrink-0" />
          )}
        </div>
      ))}
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-8">
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-blue-400 font-medium mb-1">Upload a sample file from this aggregator</h3>
                  <p className="text-slate-400 text-sm">We'll use this to understand the file structure and map columns</p>
                </div>
              </div>
            </div>

            <div className="flex flex-col items-center justify-center py-12">
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileUpload(file);
                }}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer flex items-center gap-3 px-6 py-3 bg-slate-700/50 hover:bg-slate-700 text-white rounded-lg transition-all"
              >
                <Upload className="w-5 h-5" />
                Select File (Excel or CSV)
              </label>

              {fileParseError && (
                <div className="mt-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg w-full max-w-2xl">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-red-400 font-medium mb-1">File Parsing Error</h4>
                      <p className="text-slate-400 text-sm">{fileParseError}</p>
                    </div>
                  </div>
                </div>
              )}

              {sampleFile && !fileParseError && (
                <div className="mt-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileSpreadsheet className="w-5 h-5 text-green-400" />
                    <span className="text-green-400 font-medium">{sampleFile.name}</span>
                  </div>
                </div>
              )}

              {parsedData.length > 0 && (
                <div className="mt-6 w-full">
                  <h4 className="text-white font-medium mb-3">Data Preview</h4>
                  <div className="bg-slate-900/50 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-800/50">
                          <tr>
                            {columns.slice(0, 5).map(col => (
                              <th key={col} className="px-4 py-2 text-left text-slate-400 font-medium">
                                {col}
                              </th>
                            ))}
                            {columns.length > 5 && (
                              <th className="px-4 py-2 text-slate-500">...</th>
                            )}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700/50">
                          {parsedData.slice(0, 3).map((row, idx) => (
                            <tr key={idx}>
                              {columns.slice(0, 5).map(col => (
                                <td key={col} className="px-4 py-2 text-slate-300">
                                  {row[col] || '-'}
                                </td>
                              ))}
                              {columns.length > 5 && (
                                <td className="px-4 py-2 text-slate-500">...</td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="px-4 py-2 bg-slate-800/50 text-xs text-slate-500">
                      Showing 3 of {parsedData.length} rows, {columns.length} columns
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 1:
        return (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-8">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Template Name
                </label>
                <input
                  type="text"
                  value={template.name}
                  onChange={e => setTemplate(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                  placeholder="e.g., ENTO100 Commission File"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Aggregator
                </label>
                <select
                  value={template.aggregator}
                  onChange={e => setTemplate(prev => ({ ...prev, aggregator: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="">Select aggregator</option>
                  <option value="LMG">LMG</option>
                  <option value="Loan Market">Loan Market</option>
                  <option value="AFG">AFG</option>
                  <option value="FAST">FAST</option>
                  <option value="Connective">Connective</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Header Row (0-indexed)
                </label>
                <input
                  type="number"
                  min="0"
                  value={template.parserConfig.headerRow}
                  onChange={e => setTemplate(prev => ({
                    ...prev,
                    parserConfig: {
                      ...prev.parserConfig,
                      headerRow: parseInt(e.target.value) || 0
                    }
                  }))}
                  className="w-full px-4 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                />
                <p className="text-xs text-slate-500 mt-1">Which row contains the column headers? (0 = first row)</p>
              </div>

              {template.fileType === 'xlsx' && (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Sheet Name (Optional)
                  </label>
                  <input
                    type="text"
                    value={template.parserConfig.sheetName || ''}
                    onChange={e => setTemplate(prev => ({
                      ...prev,
                      parserConfig: {
                        ...prev.parserConfig,
                        sheetName: e.target.value || undefined
                      }
                    }))}
                    className="w-full px-4 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                    placeholder="Leave empty for first sheet"
                  />
                </div>
              )}
            </div>
          </div>
        );

      case 2:
        // Check if this is the worksheet mapper step (only for multi-sheet)
        if (template.isMultiSheet && detectedSheets.length > 1) {
          return (
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-8">
              <WorksheetMapper
                detectedSheets={detectedSheets}
                worksheetMapping={template.worksheetMapping || {}}
                onChange={(mapping) =>
                  setTemplate(prev => ({ ...prev, worksheetMapping: mapping }))
                }
                suggestions={worksheetSuggestions}
              />
            </div>
          );
        }
        // Fall through to column mapper for single-sheet files
        return (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-8">
            <ColumnMapper
              sourceColumns={columns}
              currentMappings={template.columnMappings}
              onMappingsChange={(mappings) =>
                setTemplate(prev => ({ ...prev, columnMappings: mappings }))
              }
            />
          </div>
        );

      case 3:
        // Check if this is column mapper for multi-sheet (step shifted by 1)
        if (template.isMultiSheet && detectedSheets.length > 1) {
          return (
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-8">
              <ColumnMapper
                sourceColumns={columns}
                currentMappings={template.columnMappings}
                onMappingsChange={(mappings) =>
                  setTemplate(prev => ({ ...prev, columnMappings: mappings }))
                }
              />
            </div>
          );
        }
        // Fall through to payment types for single-sheet files
        return (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-8">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Payment Type Column
                </label>
                <select
                  value={template.paymentTypeConfig.columnName}
                  onChange={e => setTemplate(prev => ({
                    ...prev,
                    paymentTypeConfig: {
                      ...prev.paymentTypeConfig,
                      columnName: e.target.value
                    }
                  }))}
                  className="w-full px-4 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="">Select column containing payment types</option>
                  {columns.map(col => (
                    <option key={col} value={col}>{col}</option>
                  ))}
                </select>
              </div>

              {template.paymentTypeConfig.columnName && uniquePaymentTypes.length > 0 && (
                <>
                  {/* Validation Warning */}
                  {paymentTypeWarning && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="text-red-400 font-medium mb-1">⚠️ Warning: Unusual Payment Type Value</h4>
                          <p className="text-slate-400 text-sm">
                            The selected column contains "<span className="text-red-300 font-medium">{paymentTypeWarning.value}</span>" which {paymentTypeWarning.message}
                          </p>
                          <p className="text-slate-400 text-sm mt-2">
                            Please verify you selected the correct column. Common payment type columns are named: COMMISSION_TYPE, PAYMENT_TYPE, TYPE, CATEGORY.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <p className="text-blue-400 text-sm">
                      Found {uniquePaymentTypes.length} unique payment types
                    </p>
                  </div>

                  <PaymentTypeMapper
                    uniquePaymentTypes={uniquePaymentTypes}
                    currentMappings={template.paymentTypeConfig.customMappings || {}}
                    onMappingsChange={(mappings) =>
                      setTemplate(prev => ({
                        ...prev,
                        paymentTypeConfig: {
                          ...prev.paymentTypeConfig,
                          customMappings: mappings
                        }
                      }))
                    }
                  />
                </>
              )}
            </div>
          </div>
        );

      case 4:
        // Check if this is payment types for multi-sheet (step shifted by 1)
        if (template.isMultiSheet && detectedSheets.length > 1) {
          return (
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-8">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Payment Type Column
                  </label>
                  <select
                    value={template.paymentTypeConfig.columnName}
                    onChange={e => setTemplate(prev => ({
                      ...prev,
                      paymentTypeConfig: {
                        ...prev.paymentTypeConfig,
                        columnName: e.target.value
                      }
                    }))}
                    className="w-full px-4 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  >
                    <option value="">Select column containing payment types</option>
                    {columns.map(col => (
                      <option key={col} value={col}>{col}</option>
                    ))}
                  </select>
                </div>

                {template.paymentTypeConfig.columnName && uniquePaymentTypes.length > 0 && (
                  <>
                    {paymentTypeWarning && (
                      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <h4 className="text-red-400 font-medium mb-1">⚠️ Warning: Unusual Payment Type Value</h4>
                            <p className="text-slate-400 text-sm">
                              The selected column contains "<span className="text-red-300 font-medium">{paymentTypeWarning.value}</span>" which {paymentTypeWarning.message}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                      <p className="text-blue-400 text-sm">
                        Found {uniquePaymentTypes.length} unique payment types
                      </p>
                    </div>

                    <PaymentTypeMapper
                      uniquePaymentTypes={uniquePaymentTypes}
                      currentMappings={template.paymentTypeConfig.customMappings || {}}
                      onMappingsChange={(mappings) =>
                        setTemplate(prev => ({
                          ...prev,
                          paymentTypeConfig: {
                            ...prev.paymentTypeConfig,
                            customMappings: mappings
                          }
                        }))
                      }
                    />
                  </>
                )}
              </div>
            </div>
          );
        }
        // Fall through to business rules for single-sheet files
        return (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-8">
            <BusinessRulesEditor
              currentRules={template.runHandling}
              validation={template.validation}
              onRulesChange={(runHandling, validation) =>
                setTemplate(prev => ({ ...prev, runHandling, validation }))
              }
            />
          </div>
        );

      case 5:
        // Check if this is business rules for multi-sheet (step shifted by 1)
        if (template.isMultiSheet && detectedSheets.length > 1) {
          return (
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-8">
              <BusinessRulesEditor
                currentRules={template.runHandling}
                validation={template.validation}
                onRulesChange={(runHandling, validation) =>
                  setTemplate(prev => ({ ...prev, runHandling, validation }))
                }
              />
            </div>
          );
        }
        // Fall through to test & save for single-sheet files
        return (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-8">
            <div className="space-y-6">
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-blue-400 font-medium mb-1">Test your template before saving</h3>
                    <p className="text-slate-400 text-sm">Upload a test file to validate the template configuration</p>
                  </div>
                </div>
              </div>

              <TemplateTester
                template={template}
                sampleData={parsedData}
              />

              <div className="border-t border-slate-700 pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-white font-medium">Ready to save?</h3>
                    <p className="text-slate-400 text-sm mt-1">Your template will be available for use immediately</p>
                  </div>
                  <button
                    onClick={handleSave}
                    disabled={!template.name || !template.aggregator}
                    className="px-6 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-slate-700 text-white rounded-lg transition-colors flex items-center gap-2 font-medium"
                  >
                    <Save className="w-5 h-5" />
                    Save Template
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      case 6:
        // Test & Save for multi-sheet files
        if (template.isMultiSheet && detectedSheets.length > 1) {
          return (
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-8">
              <div className="space-y-6">
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-blue-400 font-medium mb-1">Test your multi-sheet template before saving</h3>
                      <p className="text-slate-400 text-sm">Upload a test file to validate the worksheet mapping and column configuration</p>
                    </div>
                  </div>
                </div>

                <TemplateTester
                  template={template}
                  sampleData={parsedData}
                />

                <div className="border-t border-slate-700 pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-white font-medium">Ready to save?</h3>
                      <p className="text-slate-400 text-sm mt-1">Your multi-sheet template will be available for use immediately</p>
                    </div>
                    <button
                      onClick={handleSave}
                      disabled={!template.name || !template.aggregator}
                      className="px-6 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-slate-700 text-white rounded-lg transition-colors flex items-center gap-2 font-medium"
                    >
                      <Save className="w-5 h-5" />
                      Save Template
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        }
        // No case 6 for single-sheet (only 6 steps: 0-5)
        return null;

      default:
        return null;
    }
  };

  return (
    <div className="template-builder">
      <StepIndicator />

      {renderStepContent()}

      <div className="flex items-center justify-between mt-8">
        <button
          onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
          disabled={currentStep === 0}
          className="px-4 py-2 bg-slate-700/50 hover:bg-slate-700 disabled:bg-slate-800/30 disabled:text-slate-600 text-white rounded-lg transition-colors"
        >
          Previous
        </button>

        <div className="flex items-center gap-2">
          {steps.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentStep(idx)}
              disabled={!sampleFile && idx > 0}
              className={`w-2 h-2 rounded-full transition-all ${
                idx === currentStep
                  ? 'w-8 bg-blue-500'
                  : 'bg-slate-600 hover:bg-slate-500'
              }`}
            />
          ))}
        </div>

        <button
          onClick={() => setCurrentStep(Math.min(steps.length - 1, currentStep + 1))}
          disabled={currentStep === steps.length - 1 || !sampleFile}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-800/30 disabled:text-slate-600 text-white rounded-lg transition-colors"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default TemplateBuilder;